/**
 * 导航与路由的**单一事实来源**。
 *
 * 为什么集中在这里而不是散在 router 与各组件：
 * 竞品实测发现它的"深层工作台"里要镜像一份全局导航（Logo 下拉里那 7 项），
 * 一旦两处各写各的，就会出现"从工作台跳不回某页"这类不一致。
 * 所以：菜单、路由、布局归属全部由此表推导，router 只负责渲染。
 */

export type NavKey =
  | 'home'
  | 'agent'
  | 'assets'
  | 'generation'
  | 'team'
  | 'canvas'
  | 'storyboard'
  | 'editing';

export type LayoutKind =
  /** 全局态：带左侧栏，常规页面 */
  | 'global'
  /** 全屏工作台：隐藏左侧栏，自带顶栏，专注单任务 */
  | 'workbench';

export interface NavItem {
  key: NavKey;
  /** 菜单文案（我方自定命名，不沿用竞品文案） */
  label: string;
  path: string;
  layout: LayoutKind;
  /**
   * 该页由哪个阶段交付。未交付的页面渲染"待 Px 接入"占位，
   * 而不是空白页 —— 空白会被当成 bug 报上来。
   */
  delivery: 'P0' | 'P1' | 'P2' | 'P3';
  /** 该页是否依赖 3D 导演台能力（决定是否要走 D-3 闸门） */
  needsDirector: boolean;
}

/** 左侧栏一级导航，固定 7 项。
 *  顺序为 2026-09-28 用户裁定（方案 A）：自由画布是专业版主工作面，紧跟首页；
 *  故事板（批量流水线向导）与剪辑属 P3 后置能力，排末尾。 */
export const NAV_ITEMS: readonly NavItem[] = [
  { key: 'home', label: '工作台首页', path: '/', layout: 'global', delivery: 'P1', needsDirector: false },
  { key: 'canvas', label: '自由画布', path: '/canvas', layout: 'global', delivery: 'P1', needsDirector: true },
  { key: 'agent', label: '智能助手', path: '/agent', layout: 'global', delivery: 'P2', needsDirector: false },
  { key: 'assets', label: '资产库', path: '/assets', layout: 'global', delivery: 'P1', needsDirector: false },
  { key: 'generation', label: 'AI 生成', path: '/generation', layout: 'global', delivery: 'P1', needsDirector: false },
  { key: 'team', label: '团队协作', path: '/team', layout: 'global', delivery: 'P3', needsDirector: false },
  { key: 'storyboard', label: '故事板', path: '/storyboard', layout: 'global', delivery: 'P3', needsDirector: false },
  { key: 'editing', label: '剪辑', path: '/editing', layout: 'global', delivery: 'P3', needsDirector: false },
];

/**
 * 不进菜单但可直达的路由。
 * director：导演台全屏工作台（P0-3 填 3D 视口，P0-1b 先立外壳以验证布局与 D-3 闸门）
 * diagnostics：环境自检页，排障用，专业用户/我们自己都要随时能打开
 */
export const EXTRA_ROUTES = [
  { path: '/director', layout: 'workbench', delivery: 'P0', needsDirector: true },
  { path: '/diagnostics', layout: 'global', delivery: 'P0', needsDirector: false },
] as const;

export function findNavByPath(path: string): NavItem | undefined {
  return NAV_ITEMS.find((item) => item.path === path);
}

export function isWorkbenchPath(path: string): boolean {
  if (path === '/director') {
    return true;
  }
  return findNavByPath(path)?.layout === 'workbench';
}
